import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {  useTheme, Box, Button } from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import ccfTableActionMenuPluginStyles from './ccf-table-action-menu-plugin.styles';
import {
  $deleteTableColumn,
  $getElementGridForTableNode,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn,
  $insertTableRow,
  $isTableCellNode,
  $isTableRowNode,
  $removeTableRowAtIndex,
  getTableSelectionFromTableElement,
  HTMLTableElementWithWithTableSelectionState,
  TableCellHeaderStates,
  TableCellNode,
} from '@lexical/table';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  DEPRECATED_$isGridSelection,
} from 'lexical';
import * as React from 'react';
import { ReactPortal, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HIGHLIGHT_COLOR_GROUP,CustomStyleConfig,colorData, useTranslator} from '@nice-ccf/ui-controls';


type TableCellActionMenuProps = Readonly<{
  contextRef: { current: null | HTMLElement };
  onClose: () => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  tableCellNode: TableCellNode;
}>;

/**
 * 
 * @example <TableActionMenu/>
 * @returns Table cell action menues
 */
export const TableActionMenu: React.FC<TableCellActionMenuProps> = ({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
}) => {
  const theme = useTheme();
  const [translate] = useTranslator();
  const style = ccfTableActionMenuPluginStyles(theme);
  const [editor] = useLexicalComposerContext();
  const dropDownRef = useRef<HTMLDivElement | null>(null);
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colors: CustomStyleConfig = colorData.filter(
    (color) => color.group === HIGHLIGHT_COLOR_GROUP
  );

  useEffect(() => {
    return editor.registerMutationListener(TableCellNode, (nodeMutations) => {
      const nodeUpdated = nodeMutations.get(tableCellNode.getKey()) === 'updated';
      if (nodeUpdated) {
        editor.getEditorState().read(() => {
          updateTableCellNode(tableCellNode.getLatest());
        });
      }
    });
  }, [editor, tableCellNode]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (DEPRECATED_$isGridSelection(selection)) {
        const selectionShape = selection.getShape();
        updateSelectionCounts({
          columns: selectionShape.toX - selectionShape.fromX + 1,
          rows: selectionShape.toY - selectionShape.fromY + 1,
        });
      }
    });
  }, [editor]);

  useEffect(() => {
    const menuButtonElement = contextRef.current;
    const dropDownElement = dropDownRef.current;
    if (menuButtonElement != null && dropDownElement != null) {
      const menuButtonRect = menuButtonElement.getBoundingClientRect();
      dropDownElement.style.opacity = '1';
      dropDownElement.style.left = `${
        menuButtonRect.left + menuButtonRect.width + window.pageXOffset + 5
      }px`;
      dropDownElement.style.top = `${
        menuButtonRect.top + window.pageYOffset
      }px`;
    }
  }, [contextRef, dropDownRef]);

  useEffect(() => {
    /**
     * Method to handle menu dropdown behavior when clicked outside
     * @example handleClickOutside(e);  
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        !dropDownRef.current.contains(event.target as Node) &&
        !contextRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = editor.getElementByKey(
          tableNode.getKey()
        ) as HTMLTableElementWithWithTableSelectionState;
        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM');
        }
        const tableSelection = getTableSelectionFromTableElement(tableElement);
        if (tableSelection !== null) {
          tableSelection.clearHighlight();
        }
        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }
      const rootNode = $getRoot();
      rootNode.selectStart();
    });
  }, [editor, tableCellNode]);

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        const selection = $getSelection();
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        let tableRowIndex;
        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableRowIndex = shouldInsertAfter
            ? selectionShape.toY
            : selectionShape.fromY;
        } else {
          tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
        }
        const grid = $getElementGridForTableNode(editor, tableNode);
        $insertTableRow(
          tableNode,
          tableRowIndex,
          shouldInsertAfter,
          selectionCounts.rows,
          grid
        );
        clearTableSelection();
        onClose();
      });
    },
    [editor, tableCellNode, selectionCounts.rows, clearTableSelection, onClose]
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        const selection = $getSelection();
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        let tableColumnIndex;
        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableColumnIndex = shouldInsertAfter
            ? selectionShape.toX
            : selectionShape.fromX;
        } else {
          tableColumnIndex = $getTableColumnIndexFromTableCellNode(
            tableCellNode
          );
        }
        const grid = $getElementGridForTableNode(editor, tableNode);
        $insertTableColumn(
          tableNode,
          tableColumnIndex,
          shouldInsertAfter,
          selectionCounts.columns,
          grid
        );
        clearTableSelection();
        onClose();
      });
    },
    [
      editor,
      tableCellNode,
      selectionCounts.columns,
      clearTableSelection,
      onClose
    ]
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
      $removeTableRowAtIndex(tableNode, tableRowIndex);
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableColumnIndex = $getTableColumnIndexFromTableCellNode(
        tableCellNode
      );
      $deleteTableColumn(tableNode, tableColumnIndex);
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableRowIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
      const tableRows = tableNode.getChildren();
      if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }
      const tableRow = tableRows[tableRowIndex];
      if (!$isTableRowNode(tableRow)) {
        throw new Error('Expected table row');
      }
      tableRow.getChildren().forEach((tableCell) => {
        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }
        tableCell.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableColumnIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableColumnIndex = $getTableColumnIndexFromTableCellNode(
        tableCellNode
      );
      const tableRows = tableNode.getChildren();
      tableRows.forEach(tableRow => {
        if (!$isTableRowNode(tableRow)) {
          throw new Error('Expected table row');
        }
        const tableCells = tableRow.getChildren();
        if (
          tableColumnIndex >= tableCells.length ||
          tableColumnIndex < 0 ||
          !$isTableCellNode(tableCells[tableColumnIndex])) {
          throw new Error('Expected table cell to be inside of table row and be a valid table cell.');
        }
        const tableCell = tableCells[tableColumnIndex];
        tableCell.toggleHeaderStyle(TableCellHeaderStates.COLUMN);
      });
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);


  /**
   * Method to change bg color of selected cell/s
   * @example handleCellBackgroundColor(selectedColor) 
   */
  const handleCellBackgroundColor = (value: string) => {
    const colorDetails: CustomStyleConfig = colorData.filter(color => color.id === value);
    const selectedColor = colorDetails ? colorDetails[0]?.value : '';
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if ($isTableCellNode(tableCellNode)) {
          tableCellNode.setBackgroundColor(selectedColor);
        }
      } 
      if (DEPRECATED_$isGridSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach(node => {
          if ($isTableCellNode(node)) {
            node.setBackgroundColor(selectedColor);
          }
        });
      } 
    });
  };
    

  return createPortal(
    <Box
      sx={style.dropdown}
      ref={dropDownRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {!showColorPicker && 
      <Box id='dropdown-contents' data-testid='dropdown-contents'>
        <Box data-testid='bg-color' sx={style.item} onClick={()=>{setShowColorPicker(true)}}>
          <Box component='span' sx={style.text}>
            {translate('bgColor')}
          </Box>
        </Box>
        <hr />
        <Box data-testid='insert-row-above' sx={style.item} onClick={() => insertTableRowAtSelection(false)}>
          <Box component='span' sx={style.text}>
            {translate('insert')}{' '}
            {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`}{' '}
            {translate('above')}
          </Box>
        </Box>
        <Box data-testid='insert-row-below' sx={style.item} onClick={() => insertTableRowAtSelection(true)}>
          <Box component='span' sx={style.text}>
            {translate('insert')}{' '}
            {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`}{' '}
            {translate('below')}
          </Box>
        </Box>
        <hr />
        <Box
          sx={style.item}
          data-testid='insert-row-right'
          onClick={() => insertTableColumnAtSelection(false)}
        >
          <Box component='span' sx={style.text}>
            {translate('insert')}{' '}
            {selectionCounts.columns === 1
              ? 'column'
              : `${selectionCounts.columns} columns`}{' '}
            {translate('left')}
          </Box>
        </Box>
        <Box
          sx={style.item}
          data-testid='insert-row-left'
          onClick={() => insertTableColumnAtSelection(true)}
        >
          <Box component='span' sx={style.text}>
            {translate('insert')}{' '}
            {selectionCounts.columns === 1
              ? 'column'
              : `${selectionCounts.columns} columns`}{' '}
            {translate('right')}
          </Box>
        </Box>
        <hr />
        <Box sx={style.item} data-testid='delete-column' onClick={() => deleteTableColumnAtSelection()}>
          <Box component='span' sx={style.text}>{translate('deleteColumn')}</Box>
        </Box>
        <Box sx={style.item} data-testid='delete-row' onClick={() => deleteTableRowAtSelection()}>
          <Box component='span' sx={style.text}>{translate('deleteRow')}</Box>
        </Box>
        <Box sx={style.item} data-testid='delete-table' onClick={() => deleteTableAtSelection()}>
          <Box component='span' sx={style.text}>{translate('deleteTable')}</Box>
        </Box>
        <hr />
        <Box sx={style.item} data-testid='row-header' onClick={() => toggleTableRowIsHeader()}>
          <Box component='span' sx={style.text}>
            {(tableCellNode?.__headerState & TableCellHeaderStates.ROW) ===
          TableCellHeaderStates.ROW
              ? translate('remove')
              : translate('add')}{' '}
            {translate('rowHeader')}
          </Box>
        </Box>
        <Box sx={style.item} data-testid='column-header' onClick={() => toggleTableColumnIsHeader()}>
          <Box component='span' sx={style.text}>
            {(tableCellNode?.__headerState & TableCellHeaderStates.COLUMN) ===
          TableCellHeaderStates.COLUMN
              ? translate('remove')
              : translate('add')}{' '}
            {translate('columnHeader')}
          </Box>
        </Box>
      </Box>
      }
      {showColorPicker &&
        <Box sx={style.ColorPickerPopover}>
          <Button sx={style.ColorResetButton} onClick={()=>handleCellBackgroundColor('')}>Reset</Button>
          <Box sx={style.ColorButtonContainer} >
            {colors.map(({id, value}) => (
              <Box component='button'
                title={'label'}
                sx={{ ...style.ColorButton, backgroundColor: value }}
                onClick={() => handleCellBackgroundColor(id)}
                data-testid={id}
              >
              </Box>
            ))}
          </Box>
        </Box>
      }
    </Box>,
    document.body
  );
}

/**
 * 
 * @example <TableCellActionMenuContainer/>
 * @returns Table cell action menues
 */
export function TableCellActionMenuContainer({
  anchorElem,
}: {
  anchorElem: HTMLElement;
}): JSX.Element {
  const theme = useTheme();
  const style = ccfTableActionMenuPluginStyles(theme);
  const [editor] = useLexicalComposerContext();
  const menuButtonRef = useRef(null);
  const menuRootRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(
    null
  );

  const moveMenu = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;
    if (selection == null || menu == null) {
      setTableMenuCellNode(null);
      return;
    }
    const rootElement = editor.getRootElement();
    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null);
        return;
      }
      const tableCellParentNodeDOM = editor.getElementByKey(tableCellNodeFromSelection.getKey());
      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null);
        return;
      }
      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if (!activeElement) {
      setTableMenuCellNode(null);
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        moveMenu();
      });
    });
  });

  useEffect(() => {
    const menuButtonDOM = menuButtonRef.current as HTMLButtonElement | null;
    if (menuButtonDOM != null && tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey());
      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect();
        const menuRect = menuButtonDOM.getBoundingClientRect();
        const anchorRect = anchorElem.getBoundingClientRect();
        menuButtonDOM.style.opacity = '1';

        //get element with scroll
        const richTextContainer = document.getElementById('richTextEditorBody');
        //get scroll position for to get the exact position off table cell 
        const scrollOffsetLeft = richTextContainer?.scrollLeft ?? 0;
        const scrollOffsetTop = richTextContainer?.scrollTop ?? 0;

        //adding scroll left value if there is any horizontal scroll
        menuButtonDOM.style.left = `${
          tableCellRect.right - menuRect.width - 10 - anchorRect.left + scrollOffsetLeft
        }px`;
        //adding scroll top value if there is any vertical scroll
        menuButtonDOM.style.top = `${tableCellRect.top - anchorRect.top + scrollOffsetTop + 4}px`;
      } else {
        menuButtonDOM.style.opacity = '0';
      }
    }
  }, [menuButtonRef, tableCellNode, editor, anchorElem]);

  const prevTableCellDOM = useRef(tableCellNode);

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }

    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);

  return (
    <Box sx={style.tableCellActionButtonContainer} ref={menuButtonRef} data-testid="container">
      {tableCellNode != null && (
        <>
          <Box
            sx={style.tableCellActionButton}
            data-testid='table-cell-action-button'
            onClick={() => {
              setIsMenuOpen(!isMenuOpen);
              // e.stopPropagation();
            }}
            ref={menuRootRef}
          >
            <ExpandMore fontSize="small"/>
          </Box>
          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
            />
          )}
        </>
      )}
    </Box>
  );
}

/**
 * 
 * @example <TableActionMenuPlugin/> 
 * @returns enbles cell actions on table component
 */
export default function TableActionMenuPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): ReactPortal {
  return createPortal(
    <TableCellActionMenuContainer anchorElem={anchorElem} />,
    anchorElem
  );
}
